import * as React from 'react';

import { 
    makeLayout, makeLayoutSlot, 
    makePlugin, makePluginSlot,
    makeController, effectGetter, Effects,
    makeEntry, IdentityComponent,
    IControllerContext, IPluginContext, ISlotContext, 
} from '../../core/Slot';


export interface SelectControls {}
interface Select<A extends Record<string, any> = Record<string, any>> {
    slots: {
        control: {
            innerProps: {
            },
            outerProps: {
                children: React.ReactNode,
                className?: string,
                disabled?: boolean,
            },
        },
        target: {
            innerProps: {},
            outerProps: {
                value: A,
                className?: string,
                defaultValue?: A,
            },
        },
        indicator: {
            innerProps: {},
            outerProps: {
                className?: string,
            },
        },
        dropdown: {
            innerProps: {},
            outerProps: {
                className?: string,
                width?: 'max-content' | 'select-width',
                maxHeight?: number | 'auto',
            },
        },
        dropdownTarget: {
            innerProps: {},
            outerProps: {
                className?: string,
                data?: A[],
            },
        },
    },
    controllers: {
        select: {
            id: string,
            isActive: () => boolean,
            on: () => void,
            off: () => void,
            reset: () => void,
            batch: () => void,
        },
        dropdown: {
            isOpen: () => boolean,
            open: () => void,
            close: () => void,
        },
        dropdownTarget: {
            getData: () => Record<string, any>[],
            getMutableData: () => Record<string, any>[],
            changeData: (d: Record<string, any>[]) => void,
            modifyData: (f: (d: Record<string, any>[]) => Record<string, any>[]) => void,
        },
        target: {
            tag: keyof SelectControls,
            isEmpty: () => boolean,
            change: (a: Record<string, any> | null) => void,
            getStartValue: () => A | null,
            getValue: () => A | null,
            reset: () => void,
            active: () => void,
            inactive: () => void,
        },
        cursor: {
            get: () => number,
            set: (x: number) => void,
            modify: (f: (x: number) => number) => void,
        },
        service: {
            name: string,
            onSelect: (value: unknown, name: string) => void,
            onClear: (name: string) => void,
            controlRef: () => React.RefObject<HTMLDivElement>,
        },
        action: {
            batch: () => void,
            isVisible: () => boolean,
        },
        multi: {
            get: () => Record<string, any>[],
            append: (x: Record<string, any>) => void,
            removeAt: (id: number) => void,
            clear: () => void,
        },
    },
    effects: [
        'dropdownOpened', 
        'changeTarget',
        'changeValue',
        'selectActivate',
        'selectInactivate',
        'dropdownClosed',
        'cursorMove',
        'changeData',
        'actionActive',
        'actionInactive',
        'selectReset',
        'multiChange',
    ],
    plugins: {
        keyboard: {},
        border: {},
        cursor: {},
        setter: {},
    },
}


export const URI = 'Select';
export type URI = typeof URI;
declare module '../../core/Slot' {
    interface URItoKind {
        [URI]: Select,
    }
}

const SlotContext = React.createContext({} as ISlotContext<URI>);
const ControllerContext = React.createContext({} as IControllerContext<URI>);
const EffectContext = React.createContext({} as Effects<URI>);
const PluginContext = React.createContext({} as IPluginContext<URI>);

export const Entry = makeEntry({
    SlotContext,
    ControllerContext,
    EffectContext,
    PluginContext,
    initSlots: {
        control: {
            component: IdentityComponent,
            props: { 
                children: () => IdentityComponent, 
                disabled: () => false, 
            },
        },
        target: {
            component: IdentityComponent,
            props: {
                value: () => ({ id: -1 }),
            }
        },
        indicator: {
            component: IdentityComponent,
            props: {}
        },
        dropdown: {
            component: ({ children }) => {
                return (
                    <div>{children}</div>
                );
            },
            props: {
                width: () => 'select-width' as const,
            },
        },
        dropdownTarget: {
            component: () => {
                return (
                    <div>111</div>
                );
            },
            props: {
            },
        },
    },
    initControllers: {
        select: {
            id: 'abc',
            isActive: () => false,
            on: () => {},
            off: () => {},
            reset: () => {},
            batch: () => {  },
        },
        dropdown: {
            isOpen: () => false,
            open: () => {},
            close: () => {},
        },
        dropdownTarget: {
            getData: () => [],
            getMutableData: () => [],
            changeData: () => {},
            modifyData: () => {},
        },
        target: {
            tag: 'Input',
            isEmpty: () => true,
            change: () => { console.warn('Таргет не подключен, Изменять значение не у кого!') },
            getValue: () => {
                console.warn('Таргет не подключен. Смотреть значение не у кого.');
                return {}
            },
            getStartValue: () => ({ text: '' }),
            reset: () => {  },
            active: () => {  },
            inactive: () => {  },
        },
        cursor: {
            get: () => 0,
            set: () => {},
            modify: () => {},
        },
        service: {
            name: 'empty',
            onSelect: () => {},
            onClear: () => {},
            controlRef: () => React.useRef(null),
        },
        action: {
            batch: () => {},
            isVisible: () => false,
        },
        multi: {
            get: () => [],
            append: () => {},
            removeAt: () => {},
            clear: () => {},
        },
    },
    initEffect: [
        'dropdownOpened',
        'dropdownClosed',
        'changeValue',
        'changeTarget',
        'selectActivate',
        'selectInactivate',
        'cursorMove',
        'changeData',
        'actionActive',
        'actionInactive',
        'selectReset',
        'multiChange',
    ],
});

export const Layout = makeLayout(SlotContext);
export const [useGetter, useSetter] = makeController(ControllerContext);
export const slot = makeLayoutSlot(SlotContext);
export const useEffect = effectGetter(EffectContext);
export const Plugin = makePlugin(PluginContext);
export const plugin = makePluginSlot({ PluginContext, SlotContext });
